import { useQuery } from "react-query";
import { DataCollectionStore, getCubaREST, WithId } from "@cuba-platform/react";
import { EntitiesLoadOptions, SerializedEntity } from "@cuba-platform/rest";
import { makeCollectionContainer } from "../../util";
import { EntityFilter } from "@cuba-platform/rest/dist-node/filter";

type UseDictionaryQueryOptions<T> = {
  view?: string;
  loadOptions?: EntitiesLoadOptions;
  conditions?: EntityFilter["conditions"];
  onLoaded?: (
    container: Pick<DataCollectionStore<T>, "items" | "status">,
  ) => void;
  enabled?: boolean;
};

export function useDictionary<T extends WithId>(
  entityName: string,
  options?: UseDictionaryQueryOptions<T>,
) {
  const view = options?.loadOptions?.view ?? options?.view ?? "_local";
  const loadOptions = options?.loadOptions || { view: "_local" };
  const conditions = options?.conditions;
  const onLoaded = options?.onLoaded;
  const enabled = options?.enabled ?? true;

  const fetchEntities = async (): Promise<
    Pick<DataCollectionStore<T>, "items" | "status">
  > => {
    let data: SerializedEntity<T>[];

    if (conditions?.length) {
      data = await getCubaREST().searchEntities<SerializedEntity<T>>(
        entityName,
        { conditions },
        { ...loadOptions, view },
      );
    } else {
      data = await getCubaREST().loadEntities<SerializedEntity<T>>(entityName, {
        ...loadOptions,
        view,
      });
    }

    return makeCollectionContainer(data);
  };

  return useQuery<Pick<DataCollectionStore<T>, "items" | "status">>({
    queryKey: ["dictionary", entityName, view, conditions, loadOptions],
    queryFn: fetchEntities,
    staleTime: 1000 * 60 * 10,
    enabled,
    onSuccess: (container) => {
      if (onLoaded) {
        onLoaded(container);
      }
    },
  });
}
